import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BadgeCheckIcon, LockIcon } from "lucide-react";
import { useState } from "react";
import { CredentialReport } from "@/components/credentials/credential-report";
import { ReviewQueue } from "@/components/credentials/review-queue";
import { useAccountPlan } from "@/core/hooks/use-account-plan";
import { openFeatureLock } from "@/core/lib/feature-lock-store";
import { canUseFeature, FEATURE_LABEL, type FeatureKey } from "@/core/lib/plans";
import { countReviews, reviewKeys } from "@/lib/credentials-api";
import { Badge } from "@/ui/components/ui/badge";
import { Button } from "@/ui/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/components/ui/tabs";

/**
 * Credentials: uploads waiting for review, and the report of who is current,
 * expiring, expired or missing. Records themselves live on each employee's
 * Credentials tab; types and reminders in Settings → Credentials.
 */
export const Route = createFileRoute("/_workspace/credentials")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: search.tab === "report" ? ("report" as const) : undefined,
  }),
  component: CredentialsPage,
});

function Locked({ feature, children }: { feature: FeatureKey; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed p-10 text-center">
      <LockIcon className="mx-auto size-5 text-muted-foreground" />
      <p className="mt-2 font-medium text-sm">{FEATURE_LABEL[feature]} isn't in your plan</p>
      <p className="mx-auto mt-1 max-w-md text-muted-foreground text-sm">{children}</p>
      <Button className="mt-4" onClick={() => openFeatureLock(feature)}>
        See plans
      </Button>
    </div>
  );
}

function CredentialsPage() {
  const { tab: initialTab } = Route.useSearch();
  const { plan, isLoading } = useAccountPlan();
  const canReview = canUseFeature("credentialRenewals", plan);
  const canReport = canUseFeature("credentialReports", plan);
  const pending = useQuery({
    queryKey: reviewKeys.count,
    queryFn: countReviews,
    enabled: canReview,
  });
  const [tab, setTab] = useState<string>(
    initialTab ?? ((pending.data ?? 0) > 0 || !canReport ? "review" : "report"),
  );

  return (
    <main className="flex flex-1 flex-col p-4 md:p-6">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="space-y-1">
          <p className="flex items-center gap-1.5 font-medium text-[10px] text-muted-foreground uppercase tracking-[0.25em]">
            <BadgeCheckIcon className="size-3.5" />
            Credentials
          </p>
          <h1 className="font-bold text-2xl tracking-tight">Licenses and certifications</h1>
          <p className="text-muted-foreground text-sm">
            Review renewals your people upload, and see who's current, expiring or missing
            something.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="review" className="gap-1.5">
              To review
              {pending.data ? (
                <Badge className="h-5 min-w-5 rounded-full px-1.5 tabular-nums">
                  {pending.data}
                </Badge>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="report">Report</TabsTrigger>
          </TabsList>
          <TabsContent value="review" className="pt-4">
            {isLoading ? null : canReview ? (
              <ReviewQueue />
            ) : (
              <Locked feature="credentialRenewals">
                Let your people upload renewed certificates from their portal, and approve them
                here.
              </Locked>
            )}
          </TabsContent>
          <TabsContent value="report" className="pt-4">
            {isLoading ? null : canReport ? (
              <CredentialReport />
            ) : (
              <Locked feature="credentialReports">
                See every employee's licenses and certificates at a glance, and export them for an
                audit.
              </Locked>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
